import React, {Component} from 'react';
import PropTypes from 'prop-types';
import DOMUtils from '@spothero/utils/dom';
import MapGL from 'react-map-gl';
import {Marker} from 'search/mapbox/Marker';
import {PinMarker} from 'search/mapbox/PinMarker';
import {MapRef} from 'search/mapbox/MapRef';
import {GeolocationMarker} from 'search/mapbox/GeolocationMarker';
import withConfig from 'config/withConfig';

export class MapMB extends Component {
    static propTypes = {
        viewState: PropTypes.object,
        spots: PropTypes.array.isRequired,
        location: PropTypes.shape({
            latitude: PropTypes.number,
            longitude: PropTypes.number,
        }).isRequired,
        isGeolocated: PropTypes.bool,
        onLoad: PropTypes.func,
        onViewStateChange: PropTypes.func,
        onMarkerClick: PropTypes.func,
        onDrag: PropTypes.func,
        onZoom: PropTypes.func,
        mapboxToken: PropTypes.string.isRequired,
    };
    state = {
        hoveredSpots: {},
    };
    _interactionState = {};
    _mapRef = React.createRef();

    componentDidMount() {
        document.addEventListener('spotHover', this._onSpotHover);
    }

    componentWillUnmount() {
        document.removeEventListener('spotHover', this._onSpotHover);
    }

    _onSpotHover = evt => {
        const {
            detail: {hovered, spotId},
        } = evt;

        this.setState(state => ({
            hoveredSpots: {...state.hoveredSpots, [spotId]: hovered},
        }));
    };

    _onInteractionStateChange = newState => {
        const {onDrag, onZoom} = this.props;
        const {isDragging, isZooming} = this._interactionState;

        if (isDragging && !newState.isDragging) {
            onDrag();
        }

        if (isZooming && !newState.isZooming) {
            onZoom();
        }

        this._interactionState = {...newState};
    };

    _onMarkerClick = spot => () => this.props.onMarkerClick(spot.spotId);

    _onMarkerMouseOver = spot => () => this._sendSpotHover(true, spot);

    _onMarkerMouseOut = spot => () => this._sendSpotHover(false, spot);

    _sendSpotHover = (hovered, spot) => {
        DOMUtils.sendCustomEvent('spotHover', {
            hovered,
            spotId: spot.spotId,
            latitude: spot.latitude,
            activeMarker: spot.activeMarker,
        });
    };

    get map() {
        return new MapRef(this._mapRef.current.getMap());
    }

    render() {
        const {
            viewState,
            onLoad,
            onViewStateChange,
            location,
            isGeolocated,
            spots,
            mapboxToken,
        } = this.props;

        return (
            <MapGL
                ref={this._mapRef}
                mapStyle="mapbox://styles/jtomschroeder/ck438cvpn1f961clgioqzdp50"
                mapboxApiAccessToken={mapboxToken}
                scrollZoom={false}
                touchZoom={false}
                {...viewState}
                onLoad={onLoad}
                onViewStateChange={onViewStateChange}
                onInteractionStateChange={this._onInteractionStateChange}
            >
                {isGeolocated ? (
                    <GeolocationMarker location={location} />
                ) : (
                    <PinMarker location={location} color="red" />
                )}

                {spots.map((spot, i) => {
                    const {
                        spotId,
                        latitude,
                        longitude,
                        facility: {status},
                        currency_symbol: currencySymbol,
                        selectedRate: {
                            display_price: displayPrice,
                            unavailable,
                        },
                    } = spot;

                    const isDirectLinkOnly = status === 'on_direct_link_only';
                    const isDisabled = unavailable || isDirectLinkOnly;
                    const isHovered = Boolean(this.state.hoveredSpots[spotId]);

                    return (
                        <Marker
                            key={i}
                            label={`${currencySymbol}${Math.ceil(
                                displayPrice / 100
                            )}`}
                            location={{latitude, longitude}}
                            isDisabled={isDisabled}
                            isHovered={isHovered}
                            onClick={this._onMarkerClick(spot)}
                            onMouseOver={this._onMarkerMouseOver(spot)}
                            onMouseOut={this._onMarkerMouseOut(spot)}
                        />
                    );
                })}
            </MapGL>
        );
    }
}

// eslint-disable-next-line no-duplicate-imports
export {FlyToInterpolator} from 'react-map-gl';

export default withConfig(['mapboxToken'])(MapMB);
